import React, { useState, useEffect, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import styles from './MultiStepForm.module.scss';

// Context for sharing step state with children
const MultiStepFormContext = createContext(null);

export const useMultiStepForm = () => {
  const context = useContext(MultiStepFormContext);
  if (!context) {
    throw new Error('useMultiStepForm must be used within MultiStepForm');
  }
  return context;
};

const MultiStepForm = ({
  title = 'Multi-Step Form',
  steps = [],
  initialStep = 0,
  initialStepData = null,
  onSubmit,
  onSaveAndExit,
  onStepChange,
  validateStep,
  saveButtonText = 'Save & Exit',
  nextButtonText = 'Next',
  prevButtonText = 'Back',
  submitButtonText = 'Submit',
  showBackButton = false,
  isSubmitting = false,
  className = '',
  children,
  showHeaderBackButton = false,
  onHeaderBack,
  onStepDataChange,
  exitStepIndex,
  exitButtonText = 'Exit',
  onExit,
  forcePlainExit = false,
  unlockAllSteps = false,
  submitDisabled = false,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [stepData, setStepData] = useState(() => {
    // Initialize with initialStepData if provided
    if (initialStepData && Array.isArray(initialStepData)) {
      const initialData = {};
      initialStepData.forEach((data, index) => {
        if (data) {
          initialData[index] = data;
        }
      });
      return initialData;
    }
    return {};
  });
  const [stepValidation, setStepValidation] = useState({});
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Update stepData when initialStepData changes (e.g., after async data load in edit mode)
  useEffect(() => {
    if (initialStepData && Array.isArray(initialStepData)) {
      const newStepData = {};
      initialStepData.forEach((data, index) => {
        if (data) {
          newStepData[index] = data;
        }
      });
      // Only update if there's actual data
      if (Object.keys(newStepData).length > 0) {
        setStepData(newStepData);
      }
    }
  }, [initialStepData]);

  // Expose the latest step data to the parent (e.g. for unsaved-changes checks on back navigation)
  useEffect(() => {
    if (onStepDataChange) {
      onStepDataChange(stepData);
    }
  }, [stepData, onStepDataChange]);

  // Initialize validation states. Keep validity already reported by a mounted step: steps only
  // report it when their own inputs change, so resetting it here (e.g. when the parent recreates
  // the steps array after a reload) would leave Next disabled until the user edits a field again.
  useEffect(() => {
    setStepValidation((prev) => {
      const nextValidation = {};
      steps.forEach((_, index) => {
        nextValidation[index] = prev[index] || false;
      });
      return nextValidation;
    });
  }, [steps]);

  // Check if current step is valid
  const isCurrentStepValid = stepValidation[currentStep] || false;
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;
  // submitDisabled only blocks the final submit; Next on earlier steps and Save & Exit stay usable
  const isSubmitBlocked = isLastStep && submitDisabled;

  // Update step data
  const updateStepData = (stepIndex, data) => {
    setStepData((prev) => ({
      ...prev,
      [stepIndex]: { ...(prev[stepIndex] || {}), ...data },
    }));
  };

  // Update step validation
  const updateStepValidation = (stepIndex, isValid) => {
    setStepValidation((prev) => ({
      ...prev,
      [stepIndex]: isValid,
    }));
  };

  // A step tab is navigable if it is the current/a previous step, or the immediate next step
  // when the current step is valid. Later steps are reached one at a time so tab enablement
  // always matches the Next button (edits on earlier steps can invalidate later ones).
  // When unlockAllSteps is set (e.g. editing a Live banner), any later step is reachable
  // directly as long as the current step is valid.
  const isStepClickable = (stepIndex) =>
    stepIndex <= currentStep ||
    (isCurrentStepValid && (unlockAllSteps || stepIndex === currentStep + 1));

  // Navigate to specific step
  const goToStep = (stepIndex) => {
    // Don't allow navigation if submitting
    if (isSubmitting) return;

    if (stepIndex < 0 || stepIndex >= steps.length) return;
    if (!isStepClickable(stepIndex)) return;

    // Moving forward goes through the same validation as the Next button
    // (unless all steps are unlocked, in which case jump straight to the target step)
    if (stepIndex > currentStep && !unlockAllSteps) {
      handleNext();
      return;
    }

    setCurrentStep(stepIndex);
    if (onStepChange) {
      onStepChange(stepIndex, steps[stepIndex]);
    }
  };

  // Handle next button click
  const handleNext = async () => {
    // Don't proceed if submitting
    if (isSubmitting) return;

    // Run custom validation if provided
    if (validateStep) {
      const isValid = await validateStep(currentStep, stepData[currentStep]);
      if (!isValid) return;
    }

    // Check built-in validation
    if (!isCurrentStepValid) return;
    if (isSubmitBlocked) return;

    // Mark current step as completed
    setCompletedSteps((prev) => new Set([...prev, currentStep]));

    // Move to next step or submit
    if (isLastStep) {
      if (onSubmit) {
        await onSubmit(stepData);
      }
    } else {
      setCurrentStep(currentStep + 1);
      if (onStepChange) {
        onStepChange(currentStep + 1, steps[currentStep + 1]);
      }
    }
  };

  // Handle back button click
  const handleBack = () => {
    // Don't allow navigation if submitting
    if (isSubmitting) return;

    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
      if (onStepChange) {
        onStepChange(currentStep - 1, steps[currentStep - 1]);
      }
    }
  };

  // Handle save and exit
  const handleSaveAndExit = async () => {
    if (onSaveAndExit) {
      await onSaveAndExit(stepData, currentStep);
    }
  };

  // On the designated "exit" step (e.g. the first step), mandatory fields may not be filled
  // yet, so there is nothing to save. Show a plain "Exit" that just navigates away, and only
  // switch to "Save & Exit" (and allow calling the save API) once that step's own validation
  // (isCurrentStepValid) passes. forcePlainExit shows the plain exit on every step
  // (e.g. "Cancel" while an edited form is still untouched).
  const isExitStep = exitStepIndex === currentStep;
  const showPlainExit = forcePlainExit || (isExitStep && !isCurrentStepValid);

  const handleSaveOrExitClick = () => {
    if (showPlainExit) {
      if (onExit) onExit();
      return;
    }
    handleSaveAndExit();
  };

  // Calculate progress percentage
  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  // Context value for children
  const contextValue = {
    currentStep,
    stepData: stepData[currentStep] || {},
    updateStepData: (data) => updateStepData(currentStep, data),
    setStepValid: (isValid) => updateStepValidation(currentStep, isValid),
    goToStep,
    isStepValid: isCurrentStepValid,
    allStepData: stepData,
  };

  const currentStepConfig = steps[currentStep] || {};

  return (
    <MultiStepFormContext.Provider value={contextValue}>
      <div className={`${styles.multiStepForm} ${className}`}>
        {/* Loading Overlay */}
        {isSubmitting && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}>
              <div className={styles.spinner}></div>
              <p className={styles.loadingText}>Saving changes...</p>
            </div>
          </div>
        )}
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitleWrapper}>
              {showHeaderBackButton && (
                <button
                  type="button"
                  className={styles.headerBackButton}
                  onClick={onHeaderBack}
                  aria-label="Back"
                >
                  Back
                </button>
              )}
              <h1 className={styles.headerTitle}>{title}</h1>
            </div>
            <div className={styles.headerActions}>
              <Button
                variant="secondary"
                onClick={handleSaveOrExitClick}
                disabled={showPlainExit ? isSubmitting : (!isCurrentStepValid || isSubmitting)}
                type="button"
              >
                {showPlainExit ? exitButtonText : (isSubmitting ? 'Saving...' : saveButtonText)}
              </Button>
              <Button
                variant={isCurrentStepValid && !isSubmitBlocked ? 'primary' : 'ghost'}
                onClick={handleNext}
                disabled={!isCurrentStepValid || isSubmitBlocked || isSubmitting}
                type="button"
              >
                {isSubmitting ? 'Processing...' : (isLastStep ? submitButtonText : nextButtonText)}
              </Button>
            </div>
          </div>

          {/* Stepper Navigation */}
          <div className={styles.stepperContainer}>
            <div className={styles.stepperContent}>
              <div className={styles.stepper}>
                {steps.map((step, index) => {
                  const isActive = index === currentStep;
                  const isCompleted = completedSteps.has(index);
                  const isClickable = isStepClickable(index);

                  const stepClasses = [
                    styles.step,
                    isActive && styles.active,
                    isCompleted && styles.completed,
                  ]
                    .filter(Boolean)
                    .join(' ');

                  // Determine which icon to show
                  let iconSrc = null;
                  if (step.icon) {
                    if (typeof step.icon === 'object' && (step.icon.selected || step.icon.unselected)) {
                      // Icon is an object with selected/unselected variants
                      iconSrc = isActive ? step.icon.selected : step.icon.unselected;
                    } else {
                      // Icon is a simple string path
                      iconSrc = step.icon;
                    }
                  }

                  return (
                    <button
                      key={index}
                      type="button"
                      className={stepClasses}
                      onClick={() => goToStep(index)}
                      disabled={!isClickable || isSubmitting}
                      aria-current={isActive ? 'step' : undefined}
                    >
                      <div className={styles.stepIcon}>
                        <div className={styles.stepIconInner}>
                          {iconSrc ?
                            <img
                            src={iconSrc}
                            alt={`${step.label} icon`}
                            style={{cursor: 'pointer'}}
                          />

                          :

                          (index + 1)}
                        </div>
                      </div>
                      <span className={styles.stepLabel}>{step.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Progress Bar */}
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={styles.contentWrapper}>
          <div className={styles.stepContent}>
            {currentStepConfig.title && (
              <h2 className={styles.stepContentTitle}>
                {currentStepConfig.title}
              </h2>
            )}
            {currentStepConfig.titleDescription && (
              <h3 className={styles.stepContentDescription}>
                {currentStepConfig.titleDescription}
              </h3>
            )}
            <div className={styles.stepContentBody}>
              {currentStepConfig.component || children}
            </div>

            {/* Optional Footer with Back Button */}
            {(showBackButton || currentStepConfig.showFooter) && (
              <div className={styles.footer}>
                <div className={styles.footerActions}>
                  {showBackButton && !isFirstStep && (
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      type="button"
                    >
                      {prevButtonText}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={handleSaveOrExitClick}
                    disabled={showPlainExit ? false : !isCurrentStepValid}
                    type="button"
                  >
                    {showPlainExit ? exitButtonText : saveButtonText}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleNext}
                    disabled={!isCurrentStepValid || isSubmitBlocked}
                    type="button"
                  >
                    {isLastStep ? submitButtonText : nextButtonText}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MultiStepFormContext.Provider>
  );
};

MultiStepForm.propTypes = {
  title: PropTypes.string,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.shape({
          selected: PropTypes.node,
          unselected: PropTypes.node,
        }),
      ]),
      title: PropTypes.string,
      component: PropTypes.node,
      showFooter: PropTypes.bool,
    })
  ).isRequired,
  initialStep: PropTypes.number,
  initialStepData: PropTypes.array,
  onSubmit: PropTypes.func,
  onSaveAndExit: PropTypes.func,
  onStepChange: PropTypes.func,
  validateStep: PropTypes.func,
  saveButtonText: PropTypes.string,
  nextButtonText: PropTypes.string,
  prevButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
  showBackButton: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  className: PropTypes.string,
  showHeaderBackButton: PropTypes.bool,
  onHeaderBack: PropTypes.func,
  onStepDataChange: PropTypes.func,
  exitStepIndex: PropTypes.number,
  exitButtonText: PropTypes.string,
  onExit: PropTypes.func,
  forcePlainExit: PropTypes.bool,
  unlockAllSteps: PropTypes.bool,
  submitDisabled: PropTypes.bool,
  children: PropTypes.node,
};

export default MultiStepForm;
